// client/src/components/ProtectedRoute.tsx - UPDATED WITH PROFILE COMPLETION CHECK
import React, { useState, useEffect } from 'react';
import { Navigate, useLocation, Link } from 'react-router-dom';
import { supabase } from '../supabase';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001'

interface ProtectedRouteProps {
    children: React.ReactNode;
    requireProfile?: boolean; // New prop to require completed profile
}

export default function ProtectedRoute({ children, requireProfile = false }: ProtectedRouteProps) {
    const [loading, setLoading] = useState(true);
    const [authenticated, setAuthenticated] = useState(false);
    const [emailVerified, setEmailVerified] = useState(false);
    const [hasProfile, setHasProfile] = useState(false);
    const [userType, setUserType] = useState<'attorney' | 'expert' | null>(null);
    const [userEmail, setUserEmail] = useState('');
    const location = useLocation();

    useEffect(() => {
        checkAuthAndProfile();

        // Subscribe to auth changes
        const { data: listener } = supabase.auth.onAuthStateChange((_event, session) => {
            if (session?.user) {
                setAuthenticated(true);
                setEmailVerified(!!session.user.email_confirmed_at);
                setUserEmail(session.user.email || '');
                checkProfile(session.user);
            } else {
                setAuthenticated(false);
                setEmailVerified(false);
                setHasProfile(false);
                setUserType(null);
            }
            setLoading(false);
        });

        return () => {
            listener.subscription.unsubscribe();
        };
    }, []);

    const checkAuthAndProfile = async () => {
        try {
            const { data: { session }, error } = await supabase.auth.getSession();

            if (error) {
                console.error('Auth check error:', error);
                setAuthenticated(false);
                setLoading(false);
                return;
            }

            if (session?.user) {
                setAuthenticated(true);
                setEmailVerified(!!session.user.email_confirmed_at);
                setUserEmail(session.user.email || '');
                await checkProfile(session.user);
            } else {
                setAuthenticated(false);
            }
        } catch (error) {
            console.error('Auth check error:', error);
            setAuthenticated(false);
        } finally {
            setLoading(false);
        }
    };

    const checkProfile = async (user: any) => {
        try {
            // Check if expert
            const { data: expert } = await supabase
                .from('experts')
                .select('id')
                .eq('user_id', user.id)
                .maybeSingle();

            if (expert) {
                setUserType('expert');
                setHasProfile(true);
                return;
            }

            // Check if attorney
            const { data: attorney } = await supabase
                .from('attorneys')
                .select('id')
                .eq('user_id', user.id)
                .maybeSingle();

            if (attorney) {
                setUserType('attorney');
                setHasProfile(true);
                return;
            }

            // No profile found - check user metadata for type
            const userMetadata = user.user_metadata;
            if (userMetadata?.user_type === 'expert') {
                setUserType('expert');
                setHasProfile(false);
            } else if (userMetadata?.user_type === 'attorney') {
                setUserType('attorney');
                setHasProfile(false);
            }
        } catch (error) {
            console.error('Profile check error:', error);
        }
    };

    const resendVerificationEmail = async () => {
        try {
            const { error } = await supabase.auth.resend({
                type: 'signup',
                email: userEmail,
                options: {
                    emailRedirectTo: `${window.location.origin}/confirm-signup`
                }
            });

            if (error) {
                alert('Error sending verification email: ' + error.message);
            } else {
                alert('Verification email sent! Please check your inbox and spam folder.');
            }
        } catch (error) {
            console.error('Resend verification error:', error);
            alert('Error sending verification email');
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Loading...</p>
                </div>
            </div>
        );
    }

    if (!authenticated) {
        // Redirect to login, preserving where they came from
        return <Navigate to="/login" state={{ from: location }} replace />;
    }

    if (!emailVerified) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="max-w-md w-full">
                    <div className="bg-white rounded-lg shadow-xl p-8 text-center">
                        <svg className="mx-auto h-16 w-16 text-yellow-500 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                        <h2 className="text-2xl font-bold text-gray-900 mb-4">Email Verification Required</h2>
                        <p className="text-gray-600 mb-6">
                            Please verify your email address before accessing your account.
                            We sent a verification link to <strong>{userEmail}</strong>
                        </p>
                        <div className="space-y-4">
                            <button
                                onClick={resendVerificationEmail}
                                className="w-full py-2 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                            >
                                Resend Verification Email
                            </button>
                            <button
                                onClick={() => window.location.reload()}
                                className="w-full py-2 px-4 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition"
                            >
                                I've Verified My Email
                            </button>
                            <button
                                onClick={() => supabase.auth.signOut()}
                                className="w-full py-2 px-4 text-gray-600 hover:text-gray-800 transition"
                            >
                                Sign Out
                            </button>
                        </div>
                        <p className="text-xs text-gray-500 mt-4">
                            Check your spam folder if you don't see the verification email
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    // Check if profile is required and user doesn't have one
    if (requireProfile && !hasProfile && userType) {
        const intakeRoute = userType === 'expert' ? '/expert-intake' : '/attorney-intake';
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="max-w-md w-full">
                    <div className="bg-white rounded-lg shadow-xl p-8 text-center">
                        <svg className="mx-auto h-16 w-16 text-blue-500 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                        </svg>
                        <h2 className="text-2xl font-bold text-gray-900 mb-4">Complete Your Registration</h2>
                        <p className="text-gray-600 mb-6">
                            Please complete your {userType} profile to access this feature.
                        </p>
                        <div className="space-y-4">
                            <Link
                                to={intakeRoute}
                                className="w-full py-2 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition inline-block"
                            >
                                Complete {userType === 'expert' ? 'Expert' : 'Attorney'} Registration
                            </Link>
                            <button
                                onClick={() => supabase.auth.signOut()}
                                className="w-full py-2 px-4 text-gray-600 hover:text-gray-800 transition"
                            >
                                Sign Out
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return <>{children}</>;
}